import React, { useState, useEffect } from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { Button, Typography, Box, Chip, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningIcon from "@mui/icons-material/Warning";

const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "12px",
};

// Icono personalizado del marcador
const createCustomMarkerIcon = () => {
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 32 44">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2" result="offsetblur"/>
            <feFlood flood-color="rgba(0,0,0,0.2)"/>
            <feComposite in2="offsetblur" operator="in"/>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <linearGradient id="pinGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#1976d2;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#0d47a1;stop-opacity:1" />
          </linearGradient>
        </defs>
        <g filter="url(#shadow)">
          <path d="M16 1C7.16 1 0 8.16 0 17c0 9 16 27 16 27s16-18 16-27C32 8.16 24.84 1 16 1z" fill="url(#pinGradient)"/>
          <circle cx="16" cy="15" r="6" fill="white" fill-opacity="0.95"/>
        </g>
      </svg>
    `)}`,
    scaledSize: new window.google.maps.Size(32, 44),
    origin: new window.google.maps.Point(0, 0),
    anchor: new window.google.maps.Point(16, 44),
  };
};

function MapaRepartos(props) {
  const [coordinates, setCoordinates] = useState({
    latitud: props.latitud || "",
    longitud: props.longitud || "",
    direccion_entrega: props.direccion_entrega || "",
  });

  const [mapLoaded, setMapLoaded] = useState(false);

  // Actualizar coordenadas si cambian las props
  useEffect(() => {
    setCoordinates({
      latitud: props.latitud || "",
      longitud: props.longitud || "",
      direccion_entrega: props.direccion_entrega || "",
    });
  }, [props.latitud, props.longitud, props.direccion_entrega]);

  // Verificar si hay coordenadas válidas para mostrar el marcador
  const hasValidCoordinates = coordinates.latitud && coordinates.longitud;

  // Opciones del mapa que se definen solo cuando window.google está disponible
  const getMapOptions = () => {
    if (typeof window !== "undefined" && window.google && window.google.maps) {
      return {
        streetViewControl: true,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: window.google.maps.ControlPosition.TOP_RIGHT,
          mapTypeIds: [
            window.google.maps.MapTypeId.ROADMAP,
            window.google.maps.MapTypeId.SATELLITE,
          ],
        },
        fullscreenControl: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
        gestureHandling: "greedy",
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      };
    }

    return {
      streetViewControl: true,
      mapTypeControl: true,
      fullscreenControl: true,
      zoomControl: true,
      gestureHandling: "greedy",
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
      ],
    };
  };

  return (
    <Box
      sx={{
        minWidth: "500px",
        maxWidth: "800px",
        width: "100%",
        margin: "0 auto",
        height: "520px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Contenedor para icono y texto alineados horizontalmente */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: 1.5,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {/* Texto de dirección - puede ocupar múltiples líneas */}
          <Typography
            variant="body1"
            color="text.primary"
            sx={{
              fontSize: "1rem",
              lineHeight: 1.4,
              fontWeight: "500",
              flex: 1,
              minWidth: 0,
              wordBreak: "break-word", // Dividir textos extensos
              overflowWrap: "break-word", // Ajuste del texto
            }}
          >
            {coordinates.direccion_entrega}
          </Typography>

          {/* Chip de advertencia */}
          {!hasValidCoordinates && (
            <Chip
              icon={<WarningIcon />}
              label="Sin coordenadas"
              size="small"
              color="error"
              variant="outlined"
              sx={{
                flexShrink: 0,
                fontWeight: "500",
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>

        {/* Mapa */}
        <LoadScript
          googleMapsApiKey={process.env.REACT_APP_Google_maps_key}
          libraries={["places"]}
          onLoad={() => setMapLoaded(true)}
        >
          <Box
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e0e0e0",
              mb: 2,
              position: "relative",
              flex: 1,
              minHeight: 0,
            }}
          >
            {!hasValidCoordinates ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "12px",
                  gap: 1.5,
                  p: 3,
                }}
              >
                <WarningIcon sx={{ fontSize: 48, color: "warning.main" }} />
                <Typography
                  variant="h6"
                  color="text.primary"
                  textAlign="center"
                  fontWeight="600"
                  sx={{ fontSize: "1.25rem" }}
                >
                  Mapa no disponible
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{
                    maxWidth: "400px",
                    fontSize: "0.9rem",
                  }}
                >
                  No se encontraron coordenadas válidas para mostrar la
                  ubicación en el mapa.
                </Typography>
              </Box>
            ) : (
              <>
                {!mapLoaded && (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "12px",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <div
                  style={{
                    display: mapLoaded ? "block" : "none",
                    height: "100%",
                  }}
                >
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={{
                      lat: parseFloat(coordinates.latitud),
                      lng: parseFloat(coordinates.longitud),
                    }}
                    zoom={17}
                    options={getMapOptions()}
                  >
                    <Marker
                      position={{
                        lat: parseFloat(coordinates.latitud),
                        lng: parseFloat(coordinates.longitud),
                      }}
                      draggable={false}
                      icon={mapLoaded ? createCustomMarkerIcon() : undefined}
                    />
                  </GoogleMap>
                </div>
              </>
            )}
          </Box>
        </LoadScript>

        {/* Botón Volver */}
        <Box
          mt="auto"
          sx={{
            width: "100%",
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={props.handleClose}
            fullWidth
            size="medium"
            startIcon={<ArrowBackIcon />}
            sx={{
              py: 1.25,
              borderRadius: "8px",
              fontWeight: "600",
              textTransform: "none",
              fontSize: "0.95rem",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(25, 118, 210, 0.3)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Volver
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default MapaRepartos;
